import { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import type { UserType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { UserSelected } from 'modules/user';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import WorkersAllocBar from '../WorkersAllocBar';
import WorkersTable from '../WorkersTable';
import {
    getExpendIncomeAmount,
    getExpendIncomeAmountRate,
    getExpendTeamId,
    mergeTeamWorkers,
    submitWorkers,
    sumWorkerAmountRates,
    sumWorkerAmounts,
    type ExpendTeamInfoData,
    type WorkerFormRow,
    type WorkersFormValues,
} from '../model';

interface TabWorkersProps {
    objectId: number;
    expendId: number;
    expendTeam: ExpendTeamInfoData;
    readOnly: boolean;
    onSaved: () => Promise<void> | void;
}

const TabWorkers = ({ objectId, expendId, expendTeam, readOnly, onSaved }: TabWorkersProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const [addUser, setAddUser] = useState<UserType | null>(null);

    const incomeAmount = getExpendIncomeAmount(expendTeam.expend);
    const incomeAmountRate = getExpendIncomeAmountRate(expendTeam.expend);
    const teamId = getExpendTeamId(expendTeam.expend);

    const initialRows = useMemo(
        () => mergeTeamWorkers(expendTeam.teamExecutors, expendTeam.workers),
        [expendTeam.teamExecutors, expendTeam.workers],
    );

    const initialValues: WorkersFormValues = useMemo(() => ({ rows: initialRows }), [initialRows]);

    const addSelectedUser = (
        user: UserType | null,
        rows: WorkerFormRow[],
        setFieldValue: (field: string, value: unknown) => void,
    ) => {
        if (!user?.ID) {
            setAddUser(user);
            return;
        }
        const role = user.access?.role;
        if (role !== 'executor' && role !== 'manager') {
            showToast(t('expendTeamWorkers.errRole'), 'danger');
            setAddUser(null);
            return;
        }
        if (rows.some((row) => row.userId === Number(user.ID))) {
            showToast(t('expendTeamWorkers.errDuplicate'), 'danger');
            setAddUser(null);
            return;
        }
        const next: WorkerFormRow = {
            key: String(user.ID),
            userId: Number(user.ID),
            userName: String(user.userName || ''),
            userEmail: String(user.userEmail || ''),
            userPicture: user.userPicture || null,
            amount: 0,
            amountRate: 0,
            source: 'manual',
            persisted: false,
            dt: 0,
            ownerName: '',
            ownerEmail: '',
            ownerPicture: null,
        };
        setFieldValue('rows', [...rows, next]);
        setAddUser(null);
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values, helpers) =>
                submitWorkers(values, helpers, {
                    objectId,
                    expendId,
                    incomeAmount,
                    t,
                    showToast,
                    onSuccess: onSaved,
                })
            }
        >
            {({ values, setFieldValue, handleSubmit, isSubmitting }) => {
                const allocated = sumWorkerAmounts(values.rows || []);
                const allocatedRate = sumWorkerAmountRates(values.rows || []);
                const remaining = Math.round((incomeAmount - allocated) * 100) / 100;
                const remainingRate = Math.round((incomeAmountRate - allocatedRate) * 100) / 100;
                const overLimit = remaining < 0;
                const fullyAllocated = remaining === 0;

                return (
                    <Form
                        className="d-flex flex-column h-100"
                        onSubmit={handleSubmit}
                    >
                        <div className="off-create-team-form d-flex flex-column h-100">
                            <div className="off-create-team-content px-3 pt-3">
                                {!readOnly && (
                                    <UserSelected
                                        value={addUser}
                                        smallMode
                                        title={t('expendTeamWorkers.addUser')}
                                        onChange={(user) =>
                                            addSelectedUser(user, values.rows || [], setFieldValue)
                                        }
                                    />
                                )}

                                {!teamId ? (
                                    <p className="text-muted fs-12 mb-3">
                                        {t('expendTeamWorkers.noTeam')}
                                    </p>
                                ) : null}

                                <WorkersAllocBar
                                    allocated={allocated}
                                    incomeAmount={incomeAmount}
                                    overLimit={overLimit}
                                />

                                <WorkersTable
                                    rows={values.rows || []}
                                    readOnly={readOnly}
                                    rate={rate}
                                    incomeAmount={incomeAmount}
                                    onAmountChange={(index, amount, amountRate) => {
                                        setFieldValue(`rows.${index}.amount`, amount);
                                        setFieldValue(`rows.${index}.amountRate`, amountRate);
                                    }}
                                />
                            </div>

                            <div className="p-3 offcanvas-footer off-create-team-footer">
                                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                                    <div className="text-start">
                                        <div className="text-muted">
                                            {t('expendTeamWorkers.allocated')}:{' '}
                                            <strong className="text-body">
                                                {format$Price(allocated)}
                                            </strong>
                                            {!isUahMode && (
                                                <span className="ms-2 fs-12">
                                                    {withLocalCurrency(formatPrice(allocatedRate))}
                                                </span>
                                            )}
                                        </div>
                                        {overLimit ? (
                                            <div className="text-danger">
                                                {t('expendTeamWorkers.overLimit')}
                                            </div>
                                        ) : fullyAllocated ? (
                                            <div className="text-success">
                                                {t('expendTeamWorkers.fullyAllocated')}
                                            </div>
                                        ) : (
                                            <div className="text-muted">
                                                {t('expendTeamWorkers.remaining')}:{' '}
                                                <strong className="text-body">
                                                    {format$Price(remaining)}
                                                </strong>
                                                {!isUahMode && (
                                                    <span className="ms-2 fs-12">
                                                        {withLocalCurrency(
                                                            formatPrice(remainingRate),
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <Button
                                            type="submit"
                                            variant="info"
                                            className="min100px"
                                            disabled={isSubmitting || overLimit}
                                        >
                                            {spinnerTitle(
                                                <>
                                                    <SvgSave className="me-2 mtop-2" />
                                                    {t('common:buttons.save')}
                                                </>,
                                                isSubmitting,
                                                t('common:buttons.saving'),
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default TabWorkers;
